import * as React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, Alert, DatePicker, Row, Tag } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import {
	getDisabledDate,
	getDisabledDateTime,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { dispatchersRestCrudThunksForSupplier } from '../../thunks/dispatchers_thunks';
import { isDifferentAssignedSupplier } from '../../utils/WorkOrderUtils';
import moment from 'moment-timezone';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';
import { wallTimeToUtcIso } from '../../utils/DataFormatterUtils';

const FormItem = Form.Item;

const ROLE_TAG_PROPS = {
	dispatcher: {
		label: 'Dispatcher',
		className: '!ml-2 border border-solid !border-green-4/60 !bg-green-2/60 !text-grey-8/60',
	},
	leadTech: {
		label: 'Lead Tech',
		className: '!ml-2 border border-solid !border-purple-3/60 !bg-purple-2/60 !text-purple-7/60',
	},
	cc: {
		label: 'CC',
		className: '!ml-2 border border-solid !border-cyan-7/60 !bg-cyan-7/10 !text-cyan-7/60',
	},
};

interface ScheduleServiceCallFormProps extends FormComponentProps {
	onSubmit: any;
	onCancel: any;
	visible: boolean;
	createErrors: any;
	updateErrors: any;

	isReschedule?: boolean;
	fetchMultipleFieldTechs: any;
	fetchFieldTechs: any;
	fieldTechs: any;
	fetchMultipleStockLocations: any;
	fetchStockLocations: any;
	stockLocations: any;
	creating: boolean;
	updating: boolean;
	formData: any;
	fetchSupplierDispatcher: any;
	currentUser: any;
	initialTimeZone: any;
}

class ScheduleServiceCallModalForm extends React.Component<ScheduleServiceCallFormProps, any> {
	state = {
		supplierDispatcher: null as any,
		dispatcherFieldTechs: [] as any[],
	};

	_resolveDispatcherKeys = (formData: any) => ({
		locationId:
			nullSafeGet('workOrder.locationId', formData) ||
			nullSafeGet('workOrder.location.id', formData),
		supplierFacilityId: nullSafeGet('workOrder.contractorWo', formData)
			? nullSafeGet('workOrder.contractorWo.subSupplierFacilityId', formData)
			: nullSafeGet('workOrder.supplierFacilityId', formData) || formData.supplierFacilityId,
	});

	_loadSupplierDispatcher = (locationId: any, supplierFacilityId: any) => {
		const { fetchSupplierDispatcher, fetchMultipleFieldTechs, isReschedule } = this.props;
		if (!locationId || !supplierFacilityId) {
			if (this.state.supplierDispatcher || this.state.dispatcherFieldTechs.length) {
				this.setState({ supplierDispatcher: null, dispatcherFieldTechs: [] });
			}
			return;
		}
		const dispatcherCollection = isReschedule
			? 'rescheduleServiceCallDispatcher'
			: 'scheduleServiceCallDispatcher';
		fetchSupplierDispatcher({ locationId, supplierFacilityId }, dispatcherCollection)
			.then((results) => {
				const matchingRecord = results && results.find((r) => r.locationId === locationId);
				this.setState({
					supplierDispatcher: matchingRecord || null,
					dispatcherFieldTechs: matchingRecord ? this.state.dispatcherFieldTechs : [],
				});
				if (!matchingRecord) return;
				const emails = [
					matchingRecord.dispatcher,
					...nullSafeGetOrElse('primaryTechList', matchingRecord, []),
					...nullSafeGetOrElse('ccList', matchingRecord, []),
				].filter(Boolean);
				const uniqueEmails = Array.from(new Set(emails));
				if (uniqueEmails.length === 0) return;
				fetchMultipleFieldTechs(uniqueEmails, 'scheduleServiceCallDispatcherRoles')
					.then((records) => {
						this.setState({ dispatcherFieldTechs: records || [] });
					})
					.catch(() => {});
			})
			.catch(() => {});
	};

	componentDidMount(): void {
		const { form, fetchStockLocations, formData } = this.props;

		const { locationId, supplierFacilityId } = this._resolveDispatcherKeys(formData);

		locationId &&
			fetchStockLocations({
				locationId,
			}).then((stockLocations) => {
				stockLocations &&
					stockLocations.length > 0 &&
					form.setFieldsValue &&
					form.setFieldsValue({ stockLocationIds: stockLocations.map((_) => _.id) });
			});

		this._loadSupplierDispatcher(locationId, supplierFacilityId);
	}

	componentDidUpdate(prevProps: ScheduleServiceCallFormProps): void {
		const prev = this._resolveDispatcherKeys(prevProps.formData || {});
		const next = this._resolveDispatcherKeys(this.props.formData || {});
		if (
			prev.locationId !== next.locationId ||
			prev.supplierFacilityId !== next.supplierFacilityId
		) {
			this._loadSupplierDispatcher(next.locationId, next.supplierFacilityId);
		}
	}

	_getTechRole = (email: string): 'dispatcher' | 'leadTech' | 'cc' | null => {
		const { supplierDispatcher } = this.state;
		if (!supplierDispatcher || !email) return null;
		if (email === supplierDispatcher.dispatcher) return 'dispatcher';
		if (nullSafeGetOrElse('primaryTechList', supplierDispatcher, []).includes(email))
			return 'leadTech';
		if (nullSafeGetOrElse('ccList', supplierDispatcher, []).includes(email)) return 'cc';
		return null;
	};

	_orderTechs = (records: any[]): any[] => {
		const { supplierDispatcher, dispatcherFieldTechs } = this.state;
		if (!supplierDispatcher) return records;
		const dispatcher = supplierDispatcher.dispatcher;
		const primaryTechList = nullSafeGetOrElse('primaryTechList', supplierDispatcher, []);
		const ccList = nullSafeGetOrElse('ccList', supplierDispatcher, []);
		const existingEmails = new Set(
			records.map((ft) => nullSafeGet('supplierContact.email', ft)).filter(Boolean)
		);
		const merged = [
			...records,
			...dispatcherFieldTechs.filter((ft) => {
				const email = nullSafeGet('supplierContact.email', ft);
				return email && !existingEmails.has(email);
			}),
		];
		const priority = (ft: any) => {
			const email = nullSafeGet('supplierContact.email', ft);
			if (primaryTechList.includes(email)) return 0;
			if (email === dispatcher) return 1;
			if (ccList.includes(email)) return 2;
			return 3;
		};
		return merged.sort((first, second) => priority(first) - priority(second));
	};

	render() {
		const {
			visible,
			isReschedule,
			fieldTechs,
			fetchMultipleFieldTechs,
			fetchFieldTechs,
			fetchMultipleStockLocations,
			fetchStockLocations,
			stockLocations,
			onCancel,
			onSubmit,
			form,
			formData,
			createErrors,
			updateErrors,
			currentUser,
			creating,
			updating,
			initialTimeZone,
		} = this.props;
		const { getFieldDecorator, getFieldValue } = form;
		const okText = isReschedule ? 'Reschedule' : 'Schedule';
		const titleText = isReschedule ? 'Reschedule Service' : 'Schedule Service';
		getFieldDecorator(`id`, { initialValue: formData.id });
		getFieldDecorator(`workOrderId`, { initialValue: nullSafeGet('workOrder.id', formData) });
		getFieldDecorator(`supplierFacilityId`, {
			initialValue:
				formData.supplierFacilityId || nullSafeGet('workOrder.supplierFacilityId', formData),
		});

		const differentAssignedSupplier = isDifferentAssignedSupplier(formData, currentUser);

		const leadTechnicianEmail = getFieldValue('leadTechnicianEmail');
		const additionalTechnicianEmails = getFieldValue('additionalTechnicianEmails');

		const scheduleTimeRequired =
			!leadTechnicianEmail && !nullSafeGet('0', additionalTechnicianEmails);

		const location = nullSafeGet('workOrder.location', formData);
		const serviceScheduledAtTimeZone =
			form.getFieldValue('serviceScheduledAtTimeZone') || nullSafeGet('timeZoneId', location);

		return (
			<Modal
				visible={visible}
				width={600}
				title={titleText}
				okText={okText}
				onCancel={onCancel}
				onOk={onSubmit}
				closable={false}
				confirmLoading={creating || updating}
			>
				<Form layout="vertical" className="supplierForm">
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{differentAssignedSupplier ? (
						getFieldDecorator('leadTechnicianEmail', {
							initialValue: formData.leadTechnicianEmail,
						})
					) : (
						<Form.Item label="Lead Technician">
							{getFieldDecorator('leadTechnicianEmail', {
								initialValue:
									formData.leadTechnicianEmail ||
									(nullSafeGetOrElse('workOrder.supplierFacilityId', formData, -1) ===
									nullSafeGetOrElse('workOrder.assignedPrimaryTechContact.facilityId', formData, -2)
										? nullSafeGet('workOrder.assignedPrimaryTechContact.email', formData)
										: undefined),
							})(
								<OWAsyncSelect
									stateSlice={fieldTechs}
									valueAccessor={(el) => el.email}
									allowClear={true}
									targetCollectionName="scheduleServiceCallDropdown"
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchFieldTechs(
											{ search: searchText && searchText.trim() },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleFieldTechs(ids, targetCollectionName);
									}}
									orderRecords={this._orderTechs}
									renderRecord={(ft) => {
										const email = nullSafeGet('supplierContact.email', ft);
										const role = this._getTechRole(email);
										return (
											<Select.Option key={ft.email} value={ft.email}>
												{nullSafeGet('supplierContact.nameGiven', ft)}{' '}
												{nullSafeGet('supplierContact.nameFamily', ft)} ({email})
												{role && (
													<Tag className={ROLE_TAG_PROPS[role].className}>
														{ROLE_TAG_PROPS[role].label}
													</Tag>
												)}
											</Select.Option>
										);
									}}
									sortBy={{
										sort_by: 'name',
										order: 'ascend',
									}}
								/>
							)}
						</Form.Item>
					)}
					{differentAssignedSupplier ? (
						getFieldDecorator('additionalTechnicianEmails', {
							initialValue: formData.additionalTechnicianEmails,
						})
					) : (
						<Form.Item label="Additional Technicians">
							{getFieldDecorator('additionalTechnicianEmails', {
								initialValue: formData.additionalTechnicianEmails,
							})(
								<OWAsyncSelect
									mode="multiple"
									stateSlice={fieldTechs}
									valueAccessor={(el) => el.email}
									targetCollectionName="scheduleServiceCallAdditionalTechsDropdown"
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchFieldTechs(
											{ search: searchText && searchText.trim() },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleFieldTechs(ids, targetCollectionName);
									}}
									orderRecords={this._orderTechs}
									renderRecord={(ft) => {
										const email = nullSafeGet('supplierContact.email', ft);
										const role = this._getTechRole(email);
										return (
											<Select.Option key={ft.email} value={ft.email}>
												{nullSafeGet('supplierContact.nameGiven', ft)}{' '}
												{nullSafeGet('supplierContact.nameFamily', ft)} ({email})
												{role && (
													<Tag className={ROLE_TAG_PROPS[role].className}>
														{ROLE_TAG_PROPS[role].label}
													</Tag>
												)}
											</Select.Option>
										);
									}}
									sortBy={{
										sort_by: 'name',
										order: 'ascend',
									}}
								/>
							)}
						</Form.Item>
					)}
					<DateTimeWithTimeZone
						form={form}
						location={location}
						displayName={'Scheduled Arrival Time'}
						timeZoneName={'serviceScheduledAtTimeZone'}
						dateName={'serviceScheduledAt'}
						initialValue={formData.serviceScheduledAt}
						initialTimeZone={initialTimeZone}
						minuteStep={15}
						rules={[
							{
								type: 'object',
								required: scheduleTimeRequired,
								message: 'Please select an estimated arrival time.',
							},
							{
								message: 'Service scheduled time must be after the current time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const serviceScheduledUtcIso = wallTimeToUtcIso(
										value,
										serviceScheduledAtTimeZone
									);
									const serviceScheduledUtc = moment(serviceScheduledUtcIso).utc();

									const nowUtc = moment().utc().subtract(1, 'minute');

									if (serviceScheduledUtc.isBefore(nowUtc)) {
										return Promise.reject(
											new Error('Service scheduled time must be after the current time.')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						disabledDate={getDisabledDate(moment(), serviceScheduledAtTimeZone)}
						disabledTime={getDisabledDateTime(moment(), serviceScheduledAtTimeZone)}
					/>
					{differentAssignedSupplier ? (
						getFieldDecorator('stockLocationIds', {
							initialValue: formData.stockLocationIds,
						})
					) : (
						<Form.Item label="Stock Locations">
							{getFieldDecorator('stockLocationIds', {
								initialValue: formData.stockLocationIds,
							})(
								<OWAsyncSelect
									mode="multiple"
									stateSlice={stockLocations}
									valueAccessor={(el) => el.id}
									targetCollectionName="scheduleServiceCallStockLocationDropdown"
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchStockLocations(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleStockLocations(ids, targetCollectionName);
									}}
									renderRecord={(sl) => (
										<Select.Option key={sl.id} value={sl.id}>
											{nullSafeGet('name', sl)}
										</Select.Option>
									)}
									sortBy={{
										sort_by: 'name',
										order: 'ascend',
									}}
								/>
							)}
						</Form.Item>
					)}
				</Form>
			</Modal>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	isReschedule: ownProps.isReschedule,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	currentUser: state.session.currentUser,
	fieldTechs: state.field_techs,
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleFieldTechs: (ids, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchSupplierDispatcher: (params, targetCollectionName) =>
		dispatch(dispatchersRestCrudThunksForSupplier.read(params, targetCollectionName)),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Form.create<ScheduleServiceCallFormProps>()(ScheduleServiceCallModalForm));
